import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Receipt,
  Clock,
  CreditCard,
  CalendarClock,
  ChevronRight,
  BookOpen,
  BarChart3,
  FolderKanban,
  CalendarDays,
  Banknote
} from 'lucide-react';
import api from '@/lib/api';
import { fmt, stageColors, normalizeStage } from './constants';
import { LiabilitiesByBankWidget, LIABILITIES_REFRESH_EVENT } from '@/components/LiabilitiesByBankWidget';

function StatusRow({ icon: Icon, label, value, sub, color, tabKey, onNavigate, testId }) {
  return (
    <div
      className="flex items-center justify-between py-3 px-4 rounded-md border border-border bg-card hover:bg-secondary/30 transition-colors cursor-pointer group"
      onClick={() => onNavigate(tabKey)}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${color}`}>
          <Icon className="h-4.5 w-4.5" />
        </div>
        <div>
          <p className="text-sm font-medium">{label}</p>
          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-lg font-heading font-bold tabular-nums">{value}</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
}

export function OverviewTab() {
  const [, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchOverview = useCallback(() => {
    Promise.all([
      api.get('/invoices'),
      api.get('/accounting/journal-entries'),
      api.get('/payroll-periods'),
      api.get('/accounts-payable/stats'),
      api.get('/accounting/invoice-aging'),
    ]).then(([iRes, jRes, pRes, apRes, agingRes]) => {
      const invoices = iRes.data || [];
      const je = jRes.data || [];
      const periods = pRes.data || [];
      const apStats = apRes.data || {};
      const aging = agingRes.data || {};

      const active = invoices.filter(i => i.status !== 'Cancelled');
      const totalInvoiced = active.reduce((s, i) => s + (i.total || 0), 0);
      const totalCollected = active.reduce((s, i) => s + (i.amount_paid || 0), 0);
      const totalExpenses = je
        .filter(e => e.category === 'Expense')
        .reduce((s, e) => s + Math.abs(e.debit || 0) - Math.abs(e.credit || 0), 0);
      const draftCount = invoices.filter(i => i.status === 'Draft').length;
      const sentCount = invoices.filter(i => i.status === 'Sent').length;
      const partialCount = invoices.filter(i => i.status === 'Partial').length;
      const paidCount = invoices.filter(i => i.status === 'Paid').length;
      const overdueCount = invoices.filter(i => {
        if (!i.due_date || !['Sent', 'Partial'].includes(i.status)) return false;
        return i.due_date < new Date().toISOString().slice(0, 10);
      }).length;

      const activePeriod = periods.find(p => normalizeStage(p.stage) !== 'Paid / Distributed') || periods[0];

      setData({
        totalInvoiced, totalCollected, totalExpenses,
        netIncome: totalCollected - totalExpenses,
        draftCount, sentCount, partialCount, paidCount, overdueCount,
        totalInvoiceCount: invoices.length,
        jeCount: je.length,
        payrollPeriod: activePeriod,
        apStats,
        agingTotal: aging?.totals?.total || 0,
        aging90: aging?.totals?.['90_plus'] || 0,
      });
      setLoading(false);
    }).catch(() => { setError(true); setLoading(false); });
  }, []);

  useEffect(() => {
    fetchOverview();
    // Subscribe to the same refresh bus the Liabilities widget uses. Any AP CRUD
    // or invoice mutation downstream will re-trigger the 4 financial-summary
    // cards (Total Invoiced / Collected / Expenses / Net Income) without a
    // page reload.
    const onRefresh = () => fetchOverview();
    window.addEventListener(LIABILITIES_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(LIABILITIES_REFRESH_EVENT, onRefresh);
  }, [fetchOverview]);

  if (loading) return <div className="animate-pulse text-muted-foreground p-4">Loading accounting overview...</div>;
  if (error || !data) return <div className="text-sm text-muted-foreground p-4">Unable to load overview data. Try refreshing the page.</div>;

  const nav = (tab) => setSearchParams({ tab });

  return (
    <div className="space-y-6" data-testid="accounting-overview">
      {/* Liabilities by Bank Account - sits above financials so cash-out exposure is the first thing seen */}
      <LiabilitiesByBankWidget variant="overview" />

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="overview-summary-cards">
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Total Invoiced</p>
                <p className="text-2xl font-heading font-bold mt-1 tabular-nums">{fmt(data.totalInvoiced)}</p>
              </div>
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-primary/15 text-primary">
                <Receipt className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Collected</p>
                <p className="text-2xl font-heading font-bold mt-1 text-emerald-400 tabular-nums">{fmt(data.totalCollected)}</p>
              </div>
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-emerald-500/15 text-emerald-400">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Expenses</p>
                <p className="text-2xl font-heading font-bold mt-1 text-rose-400 tabular-nums">{fmt(data.totalExpenses)}</p>
              </div>
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-rose-500/15 text-rose-400">
                <TrendingDown className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground">Net Income</p>
                <p className={`text-2xl font-heading font-bold mt-1 tabular-nums ${data.netIncome >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmt(data.netIncome)}</p>
              </div>
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-violet-500/15 text-violet-400">
                <DollarSign className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Check Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Payroll Status */}
        <Card className="border-border bg-card shadow-none" data-testid="overview-payroll-status">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" /> Payroll Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.payrollPeriod ? (
              <StatusRow
                icon={Banknote}
                label={`${data.payrollPeriod.pay_period_start} to ${data.payrollPeriod.pay_period_end}`}
                value={<Badge variant="outline" className={`text-xs ${
                  normalizeStage(data.payrollPeriod.stage) === 'Awaiting Time / Processing' ? 'bg-amber-500/15 text-amber-400' :
                  normalizeStage(data.payrollPeriod.stage) === 'Pending Review' ? 'bg-blue-500/15 text-blue-400' :
                  normalizeStage(data.payrollPeriod.stage) === 'Approved' ? 'bg-emerald-500/15 text-emerald-400' :
                  'bg-violet-500/15 text-violet-400'
                }`}>{normalizeStage(data.payrollPeriod.stage)}</Badge>}
                color="bg-amber-500/15 text-amber-400"
                tabKey="payroll"
                onNavigate={nav}
                testId="overview-go-payroll"
              />
            ) : (
              <p className="text-xs text-muted-foreground py-2">No active payroll period</p>
            )}
          </CardContent>
        </Card>

        {/* Accounts Payable Status */}
        <Card className="border-border bg-card shadow-none" data-testid="overview-ap-status">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-primary" /> Accounts Payable
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <StatusRow
              icon={CreditCard}
              label="Outstanding AP"
              value={fmt(data.apStats.outstanding_balance || 0)}
              sub={`${data.apStats.total_pending || 0} pending bills${data.apStats.overdue_count > 0 ? ` · ${data.apStats.overdue_count} overdue` : ''}`}
              color={data.apStats.overdue_count > 0 ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}
              tabKey="payable"
              onNavigate={nav}
              testId="overview-go-payable"
            />
          </CardContent>
        </Card>
      </div>

      {/* Invoice Status & Journal Entries */}
      <Card className="border-border bg-card shadow-none" data-testid="overview-invoice-status">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Receipt className="h-4 w-4 text-primary" /> Invoicing Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[
              { label: 'Draft', count: data.draftCount, color: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20' },
              { label: 'Sent', count: data.sentCount, color: 'bg-blue-500/15 text-blue-400 border-blue-500/20' },
              { label: 'Partial', count: data.partialCount, color: 'bg-amber-500/15 text-amber-400 border-amber-500/20' },
              { label: 'Paid', count: data.paidCount, color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
              { label: 'Overdue', count: data.overdueCount, color: 'bg-red-500/15 text-red-400 border-red-500/20' },
            ].map(s => (
              <div key={s.label} className="text-center p-2 border border-border rounded-md bg-secondary/10">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
                <p className="text-lg font-bold mt-0.5 tabular-nums">{s.count}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="space-y-2" data-testid="overview-quick-links">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Quick Navigation</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <StatusRow icon={BookOpen} label="Journal Entries" value={data.jeCount} sub="Manual entries" color="bg-blue-500/15 text-blue-400" tabKey="journal" onNavigate={nav} testId="overview-go-journal" />
          <StatusRow icon={FolderKanban} label="Project P&L" value="" sub="Per-project profitability" color="bg-violet-500/15 text-violet-400" tabKey="project-pl" onNavigate={nav} testId="overview-go-pl" />
          <StatusRow icon={BarChart3} label="Reports & Alerts" value="" sub="Revenue, aging, delinquency" color="bg-cyan-500/15 text-cyan-400" tabKey="reports" onNavigate={nav} testId="overview-go-reports" />
          <StatusRow icon={CalendarClock} label="Invoice Aging" value={fmt(data.agingTotal)} sub={data.agingTotal > 0 ? (data.aging90 > 0 ? `${fmt(data.aging90)} is 90+ days` : 'Outstanding receivables') : 'No aged receivables'} color={data.agingTotal > 0 ? 'bg-rose-500/15 text-rose-400' : 'bg-emerald-500/15 text-emerald-400'} tabKey="reports" onNavigate={nav} testId="overview-go-aging" />
          <StatusRow icon={CalendarDays} label="Accounting Calendar" value="" sub="Events & deadlines" color="bg-teal-500/15 text-teal-400" tabKey="acct-calendar" onNavigate={nav} testId="overview-go-calendar" />
        </div>
      </div>
    </div>
  );
}
